import { createContext, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import GenerateGroupChat from '~/components/CreateGroupChat/GenerateGroupChat/GenerateGroupChat';
import SetAiChatbot from '~/components/CreateGroupChat/SetAiChatbot/SetAiChatbot';
import { FullScreenLoading, HelmetHead } from '~/components/common';
import { useCreateGroupChatRoomMutation } from '~/features/chatRoom';
import {
  CreateGroupChatRoomRequest,
  GetMyFavoriteListData,
  Language,
  languages,
} from '~/types';
import { PAGE_ROUTE } from '~/constants';
import { useUserStore } from '~/store';
import useToast from '~/store/useToast';

export type CreateGroupChatStep = 'GenerateGroupChat' | 'SetAiChatbot';

export const MAX_SELECTED_LENGTH = 2 as const;
export const MaxSelectedArray = Array(MAX_SELECTED_LENGTH).fill(0);

export type SelectedCharacter = {
  id: string;
  name: string;
  imageUrl: string;
  description: string;
  like: number;
};
export type Accessibility = 'public' | 'private';

export const Participants: string[] = ['1', '2', '3'];

export interface CreateGroupChatState {
  setStep: (s: CreateGroupChatStep) => void;
  chatName: string;
  setChatName: (s: string) => void;
  description: string;
  setDescription: (s: string) => void;
  participants: string;
  setParticipants: (s: string) => void;
  language?: Language;
  setLanguage: (s: Language) => void;
  selectedAiCharacter: GetMyFavoriteListData[];
  accessibility: Accessibility;
  setAccessibility: (s: Accessibility) => void;
  passwordSheetOpen: boolean;
  password: string;
  setPassword: (s: string) => void;
  handleDeleteCharacterClick: (el: GetMyFavoriteListData) => void;
  handleAddCharacterClick: (el: GetMyFavoriteListData) => void;
  handleSetAiClick: () => void;
  handleGenerateClick: () => void;
  handlePrivateDoneClick: () => void;
  handlePasswordSheetClose: () => void;
  isGenerateDisabled: boolean;
}

export const CreateGroupChatContext = createContext<CreateGroupChatState>({
  setStep: () => undefined,
  chatName: '',
  setChatName: () => undefined,
  description: '',
  setDescription: () => undefined,
  participants: Participants[0],
  setParticipants: () => undefined,
  language: undefined,
  setLanguage: () => undefined,
  selectedAiCharacter: [],
  accessibility: 'public',
  setAccessibility: () => undefined,
  passwordSheetOpen: false,
  password: '',
  setPassword: () => undefined,
  handleDeleteCharacterClick: () => undefined,
  handleAddCharacterClick: () => undefined,
  handleSetAiClick: () => undefined,
  handleGenerateClick: () => undefined,
  handlePrivateDoneClick: () => undefined,
  handlePasswordSheetClose: () => undefined,
  isGenerateDisabled: false,
});

export default function CreateGroupChat() {
  const navigate = useNavigate();
  const { showToast } = useToast();
  const email = useUserStore((e) => e.email);

  const [step, setStep] = useState<CreateGroupChatStep>('GenerateGroupChat');
  const [chatName, setChatName] = useState('');
  const [description, setDescription] = useState('');

  /*=============*/
  const [participants, setParticipants] = useState<string>(Participants[0]);
  const [language, setLanguage] = useState<Language>(languages[0]);
  /*=============*/

  const [selectedAiCharacter, setSelectedAiCharacter] = useState<
    GetMyFavoriteListData[]
  >([]);
  const [accessibility, setAccessibility] = useState<Accessibility>('public');
  const [passwordSheetOpen, setPassWordSheetOpen] = useState(false);
  const [password, setPassword] = useState('');

  const { mutate: createGroupChatMutate, isPending } =
    useCreateGroupChatRoomMutation();

  const handleAddCharacterClick = (el: GetMyFavoriteListData) => {
    const index = selectedAiCharacter.findIndex(
      (s) => s.character_id === el.character_id,
    );
    if (index !== -1) {
      return;
    }
    if (selectedAiCharacter.length >= MAX_SELECTED_LENGTH) {
      return;
    }

    setSelectedAiCharacter((prev) => [...prev, el]);
  };

  const handleDeleteCharacterClick = (el: GetMyFavoriteListData) => {
    setSelectedAiCharacter((prev) =>
      prev.filter((s) => s.character_id !== el.character_id),
    );
  };

  const handleSetAiClick = () => {
    if (selectedAiCharacter.length === 0) {
      showToast('success', 'select character first');
      return;
    }

    if (selectedAiCharacter.length > MAX_SELECTED_LENGTH) {
      showToast('error', `cannot select over ${MAX_SELECTED_LENGTH}`);
      return;
    }

    setStep('GenerateGroupChat');
  };

  const handleGenerateClick = async () => {
    if (accessibility === 'private') {
      setPassWordSheetOpen(true);
      return;
    }

    if (accessibility === 'public') {
      if (
        chatName &&
        description &&
        language &&
        accessibility &&
        participants &&
        selectedAiCharacter
      ) {
        const requestData: CreateGroupChatRoomRequest = {
          name: chatName,
          description: description,
          language: language,
          accessibility: accessibility,
          password: '',
          max_participants_num: Number(participants),
          character_ids: selectedAiCharacter.map((el) => el.character_id),
        };

        createGroupChatMutate(requestData, {
          onSuccess: ({ status, data }) => {
            navigate(`${PAGE_ROUTE.GROUP_CHAT}/${data.groupchat_id}`, {
              replace: true,
            });
          },
        });
      }

      return;
    }
  };

  const handlePrivateDoneClick = async () => {
    if (
      chatName &&
      description &&
      language &&
      accessibility &&
      participants &&
      selectedAiCharacter
    ) {
      const requestData: CreateGroupChatRoomRequest = {
        name: chatName,
        description: description,
        language: language,
        accessibility: accessibility,
        password: password,
        max_participants_num: Number(participants),
        character_ids: selectedAiCharacter.map((el) => el.character_id),
      };

      createGroupChatMutate(requestData, {
        onSuccess: ({ data, status }) => {
          navigate(`${PAGE_ROUTE.GROUP_CHAT}/${data.groupchat_id}`, {
            replace: true,
          });
        },
      });
    }
  };

  const handlePasswordSheetClose = () => {
    setPassWordSheetOpen(false);
  };

  const isGenerateDisabled =
    !chatName ||
    !description ||
    !accessibility ||
    !language ||
    !selectedAiCharacter.length;

  const value: CreateGroupChatState = {
    setStep,
    chatName,
    setChatName,
    description,
    setDescription,
    participants,
    setParticipants,
    language,
    setLanguage,
    selectedAiCharacter,
    accessibility,
    setAccessibility,
    passwordSheetOpen,
    password,
    setPassword,
    handleAddCharacterClick,
    handleDeleteCharacterClick,
    handleSetAiClick,
    handleGenerateClick,
    handlePrivateDoneClick,
    handlePasswordSheetClose,
    isGenerateDisabled,
  };

  return (
    <>
      <HelmetHead title='New Chat' />

      <CreateGroupChatContext.Provider value={value}>
        {isPending && <FullScreenLoading />}
        {step === 'GenerateGroupChat' && <GenerateGroupChat />}
        {step === 'SetAiChatbot' && <SetAiChatbot />}
      </CreateGroupChatContext.Provider>
    </>
  );
}
